import { useAsyncData, useNuxtData } from '#imports'
import { computed } from 'vue'

import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'

import type { GetListingsFiltersMetadataResponse } from '~/api/listings/types'
import { useSellerInfoStore } from '~/scopes/auth/stores/sellerInfoStore'

import { OPTION_VALUE_ALL } from '../components/Filters/Filters.constants'
import translations from '../components/Filters/Filters.translations'

export function useFiltersOptions() {
  const i18n = useI18n()
  const { activeMarkets } = useSellerInfoStore()
  const { allMarkets } = useMarketplace()

  const http = useHttpFetch()

  const { data: metadata } = useAsyncData(
    'listings-metadata',
    () => http('/api/seller-experience/listings/metadata/search'),
    {
      dedupe: 'cancel',
      getCachedData: (key) =>
        useNuxtData<GetListingsFiltersMetadataResponse>(key).data.value ||
        undefined,
    },
  )

  const genericOptionAll = {
    label: i18n(translations.all),
    value: OPTION_VALUE_ALL,
  }

  const allOptions = {
    backbox: i18n(translations.backboxAll),
    grade: i18n(translations.gradesAll),
    markets: i18n(translations.marketsAll),
    stock: i18n(translations.withAndWithoutStock),
    categories: i18n(translations.categoryAll),
    hasNewBattery: i18n(translations.hasNewBatteryAll),
  }

  const hasNewBatteryOptions = [
    { label: i18n(translations.hasNewBatteryAll), value: OPTION_VALUE_ALL },
    { label: i18n(translations.hasNewBatteryNew), value: 'true' },
    { label: i18n(translations.hasNewBatteryRefurbished), value: 'false' },
  ]

  const backboxOptions = [
    { label: i18n(translations.backboxAll), value: OPTION_VALUE_ALL },
    { label: i18n(translations.withBackbox), value: 'true' },
    { label: i18n(translations.withoutBackbox), value: 'false' },
  ]

  const stockOptions = [
    { label: i18n(translations.withAndWithoutStock), value: OPTION_VALUE_ALL },
    { label: i18n(translations.withStock), value: 'true' },
    { label: i18n(translations.withoutStock), value: 'false' },
  ]

  const availableMarketsOptions = [
    genericOptionAll,
    ...activeMarkets
      .map((market) => ({
        label: i18n.country(market),
        value: market,
      }))
      .sort((a, b) => a.label.localeCompare(b.label)),
  ]

  const gradesOptions = computed(() => [
    { label: i18n(translations.gradesAll), value: OPTION_VALUE_ALL },
    ...(metadata.value?.authorizedGrades || []).map((grade) => ({
      label: grade.label,
      value: grade.code,
    })),
  ])

  const backMarketRangeCurrencyOptions = computed(() => {
    const currencies = [
      ...new Set(
        activeMarkets.map((market) => allMarkets[market]?.defaultCurrency),
      ),
    ].map((currency) => ({
      label: `${currency} (${i18n.currencySymbol(currency)})`,
      value: currency,
    }))

    return currencies
  })

  const quickFiltersValues = computed(() => {
    return metadata.value?.backboxPriceDifferenceFastFilter.map((filter) => ({
      label: i18n(translations.backboxPriceRangeButton, {
        priceRange: i18n.price(filter.maxRange, {
          currency: filter.currencyCode,
        }),
      }),
      minRange: filter.minRange,
      maxRange: filter.maxRange,
      rangeType: filter.rangeType,
      currencyCode: filter.currencyCode,
    }))
  })

  const pricingStrategyOptions = [
    { label: i18n(translations.deal), value: 'DEAL' },
  ]

  return {
    allOptions,
    availableMarketsOptions,
    backboxOptions,
    gradesOptions,
    hasNewBatteryOptions,
    stockOptions,
    quickFiltersValues,
    backMarketRangeCurrencyOptions,
    pricingStrategyOptions,
  }
}
